import React, { useMemo } from "react";
import { User, KeyRound, Clock } from "lucide-react";
import { useAllKyc, useAllUsers } from "../../services/queries/queries";

const StatCard = ({ title, value, color, isLast, type }) => {
  const colorVariants = {
    green: "bg-emerald-50 text-emerald-500",
    blue: "bg-blue-50 text-blue-500",
    orange: "bg-orange-50 text-orange-500",
  };

  const getIcon = (type) => {
    switch (type) {
      case "users":
        return <User className="w-6 h-6" />;
      case "key":
        return <KeyRound className="w-6 h-6" />;
      case "pending":
        return <Clock className="w-6 h-6" />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`p-6 bg-white rounded-lg ${
        !isLast ? "md:border-r" : ""
      } border-gray-200`}
    >
      <div
        className={`w-12 h-12 rounded-full ${colorVariants[color]} flex items-center justify-center mb-4`}
      >
        {getIcon(type)}
      </div>
      <div className="space-y-2">
        <h3 className="text-3xl font-bold text-[#013941]">
          {value.toLocaleString()}
        </h3>
        <p className="text-sm text-[#828282] uppercase tracking-wide">
          {title}
        </p>
      </div>
    </div>
  );
};

const KYCDashboard = () => {
  const {
    data: kycData,
    isLoading: kycLoading,
    isError: kycError,
  } = useAllKyc();
  const {
    data: userData,
    isLoading: userLoading,
    isError: userError,
  } = useAllUsers(1, 100);

  const stats = useMemo(() => {
    if (!kycData) return [];

    const totalUsers = userData?.data?.length || 0;
    const pendingKYC = kycData.data?.filter(
      (item) => item.status === "pending"
    ).length;
    const declinedKYC = kycData.data?.filter(
      (item) => item.status === "declined"
    ).length;
    const approvedKYC = kycData.data?.filter(
      (item) => item.status === "approved"
    ).length;

    return [
      {
        title: "Total Users",
        value: totalUsers,
        color: "green",
        type: "users",
      },
      {
        title: "Approved KYC",
        value: approvedKYC,
        color: "blue",
        type: "key",
      },
      {
        title: "Pending KYC",
        value: pendingKYC,
        color: "orange",
        type: "pending",
      },
    ];
  }, [kycData, userData]);

  if (kycLoading || userLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (kycError || userError) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-red-600">Error loading dashboard data</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 border-gray-100 rounded-lg shadow-sm border">
      {stats.map((stat, index) => (
        <StatCard
          key={stat.title}
          title={stat.title}
          value={stat.value}
          color={stat.color}
          type={stat.type}
          isLast={index === stats.length - 1}
        />
      ))}
    </div>
  );
};

export default KYCDashboard;
